/**
 * @file crm-customers-query.ts
 * @description Состояние реестра клиентов в адресе страницы (phase19.31, зона
 * «Клиенты и сделки»): поиск, вид клиента, «с блокировкой», страница и её
 * размер. Фильтры применяются сразу — кнопки «Применить» нет.
 */

import type { CustomerType, customers } from "./api-customers";
import {
  CRM_DEFAULT_PAGE_SIZE,
  pageSizeFromParam,
  pageToOffset,
  positiveInt,
} from "./crm-list-query";

export interface CustomersRegistryQuery {
  /** Имя, почта или телефон. */
  search: string;
  /** Вид клиента; пусто — все. */
  type: "" | CustomerType;
  /** Только клиенты с действующей блокировкой. */
  blockedOnly: boolean;
  page: number;
  pageSize: number;
}

const KEY = { search: "q", type: "type", blockedOnly: "blocked", page: "page", pageSize: "size" } as const;

const CUSTOMER_TYPES: readonly CustomerType[] = ["INDIVIDUAL", "LEGAL"];

export function parseCustomersQuery(params: URLSearchParams): CustomersRegistryQuery {
  const type = params.get(KEY.type) ?? "";
  return {
    search: (params.get(KEY.search) ?? "").trim(),
    type: (CUSTOMER_TYPES as readonly string[]).includes(type) ? (type as CustomerType) : "",
    blockedOnly: params.get(KEY.blockedOnly) === "1",
    page: positiveInt(params.get(KEY.page), 1),
    pageSize: pageSizeFromParam(params.get(KEY.pageSize)),
  };
}

export function writeCustomersQuery(query: CustomersRegistryQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.search) params.set(KEY.search, query.search);
  if (query.type) params.set(KEY.type, query.type);
  if (query.blockedOnly) params.set(KEY.blockedOnly, "1");
  if (query.page > 1) params.set(KEY.page, String(query.page));
  if (query.pageSize !== CRM_DEFAULT_PAGE_SIZE) params.set(KEY.pageSize, String(query.pageSize));
  return params;
}

export function toListCustomersParams(query: CustomersRegistryQuery): NonNullable<Parameters<typeof customers.list>[0]> {
  return {
    search: query.search || undefined,
    type: query.type || undefined,
    hasBlocks: query.blockedOnly || undefined,
    ...pageToOffset(query.page, query.pageSize),
  };
}

/** Фильтры шторки на телефоне: вид клиента и «с блокировкой». */
export function customersSheetFilterCount(query: CustomersRegistryQuery): number {
  return [query.type !== "", query.blockedOnly].filter(Boolean).length;
}

export function customersQueryFiltered(query: CustomersRegistryQuery): boolean {
  return query.search !== "" || customersSheetFilterCount(query) > 0;
}

export function resetCustomersQuery(query: CustomersRegistryQuery): CustomersRegistryQuery {
  return { search: "", type: "", blockedOnly: false, page: 1, pageSize: query.pageSize };
}
